import React from 'react';
import { Head } from 'vite-react-ssg';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://djpartners.es';
const SITE_NAME = 'D&J Partners';
const LOGO_URL = `${SITE_URL}/uploads/ad367c9b-b41f-46b7-a483-c58506105dbe.webp`;

interface SEOProps {
  title?: string;
  description?: string;
  type?: string;
  name?: string;
  imageUrl?: string;
  publishDate?: string;
  modifiedDate?: string;
  author?: string;
  category?: string;
  keywords?: string[];
  isBlogPost?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title = 'D&J Partners',
  description = 'D&J Partners: Transformamos PYMEs con Inteligencia Artificial práctica. Sistemas inteligentes, automatización de procesos y herramientas de gestión que impulsan el crecimiento.',
  type = 'website',
  name = SITE_NAME,
  imageUrl = '/og-image.png',
  publishDate,
  modifiedDate,
  author,
  category,
  keywords = [
    'inteligencia artificial',
    'consultoría IA',
    'transformación digital para PYMES',
    'automatización de procesos empresariales',
    'cuadros de mando',
    'business intelligence',
    'PYME',
  ],
  isBlogPost = false,
}) => {
  const location = useLocation();
  const currentUrl = `${SITE_URL}${location.pathname}`;
  const absoluteImageUrl = imageUrl.startsWith('http') ? imageUrl : `${SITE_URL}${imageUrl}`;

  // Organization JSON-LD
  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: LOGO_URL,
    description: 'Consultoría en Inteligencia Artificial y datos para PYMEs: cuadros de mando, automatización de procesos y sistemas inteligentes de gestión.',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'jordi.reina@kratia.ai',
    },
  };

  // BlogPosting JSON-LD (solo en artículos del blog)
  const blogPostStructuredData = isBlogPost && publishDate ? {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': currentUrl,
    },
    headline: title,
    image: {
      '@type': 'ImageObject',
      url: absoluteImageUrl,
      width: 1200,
      height: 630,
    },
    datePublished: publishDate,
    dateModified: modifiedDate || publishDate,
    author: {
      '@type': 'Organization',
      name: author || SITE_NAME,
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: LOGO_URL,
        width: 512,
        height: 512,
      },
      url: SITE_URL,
    },
    description,
    keywords: keywords.join(', '),
    articleSection: category,
    inLanguage: 'es-ES',
    isAccessibleForFree: true,
  } : null;

  const keywordString = category
    ? [...keywords, category.toLowerCase()].join(', ')
    : keywords.join(', ');

  return (
    <Head>
      {/* Primero: el validador de charset exige verlo en los primeros 1024 bytes,
          y vite-react-ssg inyecta las etiquetas de helmet antes que el template. */}
      <meta charSet="UTF-8" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={currentUrl} />
      <meta name="keywords" content={keywordString} />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={isBlogPost ? 'article' : type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_ES" />
      {isBlogPost && category && <meta property="article:section" content={category} />}
      {isBlogPost && publishDate && <meta property="article:published_time" content={publishDate} />}
      {isBlogPost && modifiedDate && <meta property="article:modified_time" content={modifiedDate} />}
      {isBlogPost && <meta property="article:publisher" content={SITE_URL} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImageUrl} />

      {/* LinkedIn specific */}
      <meta property="og:image:secure_url" content={absoluteImageUrl} />
      <meta name="author" content={author || name} />

      {/* Pinterest specific */}
      <meta name="pinterest:description" content={description} />
      <meta name="pinterest:image" content={absoluteImageUrl} />

      {/* Additional SEO meta tags */}
      <meta name="theme-color" content="#000000" />
      <meta name="msapplication-TileColor" content="#000000" />

      {/* JSON-LD structured data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationStructuredData)}
      </script>

      {blogPostStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(blogPostStructuredData)}
        </script>
      )}
    </Head>
  );
};

export default SEO;
